/**
 * Sysadmin Tenant Detail — Compliance tab (Wave 3 §10.2 #2 / spec §4.5).
 *
 * v1 scope: framework selector with the dual-sign reconciliation hook
 * already implemented in W1a-23. Toggling ITAR or FedRAMP fires
 * `reconcileSigningPolicy()` server-side so the tenant's bundle-signing
 * policy auto-upgrades to the FIPS dual signature scheme.
 *
 * v2 (Wave 4): full editor with derived guardrail profile, retention
 * period, audit-witness requirement, classification taxonomy in scope,
 * required user attestations.
 */

import { useState } from 'react';
import { createFileRoute, useParams } from '@tanstack/react-router';
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import {
  useTenantDetail,
  useUpdateTenantFeatures,
  type ComplianceFramework,
} from '@/lib/use-tenants';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/sysadmin/tenants/$tenantId/compliance')({
  component: TenantComplianceTab,
});

const COMPLIANCE_OPTIONS: Array<{ key: ComplianceFramework; label: string; tone: string; description: string }> = [
  { key: 'soc2',     label: 'SOC 2 Type II',          tone: 'commercial',         description: 'Service organization controls. No signing-policy impact.' },
  { key: 'iso27001', label: 'ISO 27001',              tone: 'commercial',         description: 'Information security management. No signing-policy impact.' },
  { key: 'hipaa',    label: 'HIPAA',                  tone: 'industry',           description: 'BAA tracking + PHI handling. No signing-policy impact.' },
  { key: 'fedramp',  label: 'FedRAMP Moderate',       tone: 'gov',                description: 'Triggers FIPS dual-signature bundle policy automatically.' },
  { key: 'itar',     label: 'ITAR (22 CFR §120-130)', tone: 'export-controlled',  description: 'Triggers FIPS dual-signature bundle policy automatically.' },
];

function TenantComplianceTab() {
  const { tenantId } = useParams({ from: '/sysadmin/tenants/$tenantId/compliance' });
  const { data } = useTenantDetail(tenantId);
  const [draft, setDraft] = useState<ComplianceFramework[] | null>(null);
  const [savedHint, setSavedHint] = useState<{ kind: 'success' | 'error'; msg: string } | null>(null);
  const update = useUpdateTenantFeatures();

  if (!data) return null;
  const { tenant, complianceFrameworks } = data;
  const current = (draft ?? (complianceFrameworks as ComplianceFramework[])).slice();
  const dirty = draft !== null &&
    JSON.stringify(current.slice().sort()) !==
      JSON.stringify((complianceFrameworks as string[]).slice().sort());

  const handleToggle = (key: ComplianceFramework, next: boolean) => {
    const set = new Set(current);
    if (next) set.add(key); else set.delete(key);
    setDraft([...set] as ComplianceFramework[]);
  };

  const handleReset = () => {
    setDraft(null);
    setSavedHint(null);
  };

  const handleSave = async () => {
    setSavedHint(null);
    try {
      const res = await update.mutateAsync({
        id: tenant.id,
        body: { complianceFrameworks: current },
      });
      setDraft(null);
      setSavedHint({
        kind: 'success',
        msg: res.signingPolicyReconciled
          ? 'Saved. Signing policy reconciled to FIPS dual-signature.'
          : 'Saved.',
      });
      setTimeout(() => setSavedHint(null), 4_000);
    } catch (e) {
      setSavedHint({
        kind: 'error',
        msg: e instanceof Error ? e.message : 'Save failed',
      });
    }
  };

  return (
    <div className="space-y-5">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Compliance frameworks</CardTitle>
          <CardDescription>
            Frameworks declared as obligations on this tenant. Selecting
            ITAR or FedRAMP automatically reconciles the tenant's
            bundle-signing policy to the FIPS dual-signature scheme.
            Once a framework is set, the tenant cannot relax below it.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {COMPLIANCE_OPTIONS.map((opt) => {
            const checked = current.includes(opt.key);
            return (
              <label
                key={opt.key}
                className="flex items-center justify-between gap-3 px-3 py-2 rounded border cursor-pointer hover:bg-accent"
              >
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium">{opt.label}</div>
                  <div className="text-xs text-muted-foreground">
                    {opt.description}
                  </div>
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground mt-1">
                    {opt.tone}
                  </div>
                </div>
                <Switch
                  checked={checked}
                  onCheckedChange={(v) => handleToggle(opt.key, v)}
                />
              </label>
            );
          })}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Derived obligations (v2)</CardTitle>
          <CardDescription>
            Future expansion: derived guardrail profile, retention period,
            audit-witness requirement, classification taxonomy in scope,
            required user attestations (US Person, BAA, etc.). Tracked in
            spec §4.5.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="rounded border bg-muted/30 p-4 text-sm text-muted-foreground">
            Coming in Wave 4 governance expansion.
          </div>
        </CardContent>
      </Card>

      <div className="sticky bottom-4 flex items-center justify-end gap-2 bg-background/95 backdrop-blur p-3 rounded border shadow-md">
        {savedHint && (
          <span
            className={cn(
              'text-xs mr-auto',
              savedHint.kind === 'success' ? 'text-green-600' : 'text-destructive',
            )}
          >
            {savedHint.msg}
          </span>
        )}
        {dirty && (
          <Button variant="outline" size="sm" onClick={handleReset} disabled={update.isPending}>
            Reset
          </Button>
        )}
        <Button size="sm" onClick={handleSave} disabled={!dirty || update.isPending}>
          {update.isPending ? 'Saving…' : 'Save changes'}
        </Button>
      </div>
    </div>
  );
}
